import { Table } from '@dixa/lib-shared-habla';
import { QueueMetric } from 'lib-dashboard-types';
import { QueueWithAssignmentCounts } from 'lib-shared-types-api';
import { forwardRef, Ref, useMemo } from 'react';

import { useServerStorage } from 'utils/useServerStorage';

import { DashboardCard } from '../../../../../../components/card';
import { QueueDashboardMetrics } from '../metrics/queueDashboardMetrics';

import { QueueDashboardQueueActions } from './components/actions/queueDashboardQueueActions';
import { QueueDashboardQueueTitle } from './components/title/queueDashboardQueueTitle';
import {
  QueueDashboardQueueContext,
  QueueDashboardQueueContextValue,
} from './queueDashboardQueue.context';

type QueueDashboardQueueProps = {
  /** The queue. */
  queue: QueueWithAssignmentCounts;
  /** Whether to show the skeleton UI, e.g. when loading. */
  isSkeleton?: boolean;
  /** The metrics for the current dashboard queue. */
  metrics?: readonly QueueMetric[];
};

export const QueueDashboardQueue = forwardRef<
  HTMLDivElement | HTMLTableRowElement,
  QueueDashboardQueueProps
>(function QueueDashboardQueue(
  { queue, isSkeleton = false, metrics },
  ref,
): JSX.Element {
  const storage = useServerStorage('user');
  const { layout } = storage.data.dashboard ?? {};

  const contextValue: QueueDashboardQueueContextValue = useMemo(
    () => ({
      queue,
      isSkeleton,
      layout,
    }),
    [isSkeleton, queue, layout],
  );

  const renderCompactLayout = (): JSX.Element => (
    <Table.Tr ref={ref as Ref<HTMLTableRowElement>}>
      <Table.Td>
        <QueueDashboardQueueTitle />
      </Table.Td>

      <QueueDashboardMetrics metrics={metrics} />

      <Table.Td>
        <QueueDashboardQueueActions />
      </Table.Td>
    </Table.Tr>
  );

  const renderDefaultLayout = (): JSX.Element => (
    <DashboardCard
      ref={ref as Ref<HTMLDivElement>}
      titleSlot={<QueueDashboardQueueTitle />}
      actionsSlot={<QueueDashboardQueueActions />}
    >
      <QueueDashboardMetrics metrics={metrics} />
    </DashboardCard>
  );

  return (
    <QueueDashboardQueueContext.Provider value={contextValue}>
      {layout === 'compact' ? renderCompactLayout() : renderDefaultLayout()}
    </QueueDashboardQueueContext.Provider>
  );
});
